import React from 'react';
import moment from 'moment';
import { CTableRow, CTableDataCell, CCol, CRow } from '@coreui/react';
import { Link } from "react-router-dom";

import { DeleteIcon, RightArrowLgIcon } from '../../assets/images';
import { SIM_PATHS } from '../../constants/path';
import { deleteSimTemplate, updateSimTemplate } from '../../services/simAPI';
import { pushToast } from '../../slice/appSlice';
import { selectAuth } from '../../slice/authSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { characterAndNumberOnlyInput } from '../../utils/formValidateHelper';
import CXFPConfigTextInput from '../CXForm/CXFPConfigTextInput';

import Access from '../../components/Access';
import { AccessType, UserGroupIDType } from '../../constants/roleAccessConstant';


interface ISimOFPTemplateTableItemProps {
  std: string;
  aircraftType: string;
  aircraftReg: string;
  ufi: string;
  tag: string;
  flightNum: string;
  flightNumSuffix: string;
  createdBy: string;
  createTime: string;
  simCode: string;
  templateName: string;
  routePath: string;
  onMutateDone?: () => void;
}

const RESTORE_FLAG_KEY = 'simTemplateConfiguration.restoreOnNextMount';
const AUTO_SEARCH_FLAG_KEY = 'simTemplateConfiguration.autoSearchOnMount';
export const TAG_UPDATE_PERMISSION_ERROR = 'This tag can only be updated by the record creator or an administrator.';
export const TAG_UPDATE_FAILED_ERROR = 'Failed to update SIM template tag. Please try again.';

export const isSimTemplateTagUpdateAllowed = (
  userName?: string,
  createdBy?: string,
  userGroupIDs?: string[],
) => {
  const normalizeUser = (value?: string) => (value || '').trim().toLowerCase();
  const isAdmin = userGroupIDs?.includes(UserGroupIDType.CX_SIM_ADMIN_ALL);
  const isCreator = !!normalizeUser(userName) && normalizeUser(userName) === normalizeUser(createdBy);

  return isCreator || isAdmin;
};

const SimOFPTemplateTableItem = ({ std, aircraftType, aircraftReg, ufi, tag, flightNum, flightNumSuffix, createdBy, createTime, simCode, templateName, routePath, onMutateDone }: ISimOFPTemplateTableItemProps) => {
  const dispatch = useAppDispatch();
  const { userName, userGroupIDs } = useAppSelector(selectAuth);
  const canUpdateTag = isSimTemplateTagUpdateAllowed(userName, createdBy, userGroupIDs);

  const markRestoreOnNextMount = () => {
    try {
      sessionStorage.setItem(RESTORE_FLAG_KEY, '1');
      sessionStorage.setItem(AUTO_SEARCH_FLAG_KEY, '1');
    } catch {
      // ignore
    }
  };

  const deleteTemplate = (ufi: string) => {
    if (window.confirm(`Are you sure to delete SIM Template(${ufi})?`)) {
      deleteSimTemplate(ufi)
        .then(() => onMutateDone?.())
        .catch((err) => console.log(err));
    }
  };

  const onBlurTag = async (content: any, objectKey: string, originalTag: string) => {
    const nextTag = String(content ?? '');
    const prevTag = String(originalTag ?? '');

    if (nextTag === prevTag) {
      return true;
    }

    if (!canUpdateTag) {
      dispatch(pushToast({ type: 'error', message: TAG_UPDATE_PERMISSION_ERROR }));
      return false;
    }

    try {
      await updateSimTemplate(objectKey, { tag: nextTag });
      onMutateDone?.();
      return true;
    } catch (err: any) {
      dispatch(pushToast({ type: 'error', message: err?.message || TAG_UPDATE_FAILED_ERROR }));
      return false;
    }
  };

  const ufiChecker = () => {
    if (characterAndNumberOnlyInput(ufi)) {
      return SIM_PATHS.simTemplateDetail.replace(":ufi", ufi)
    } else {
      return encodeURIComponent(SIM_PATHS.simTemplateDetail.replace(":ufi", ufi))
    }
  };

  const RobotoMonoRgDataCell = ({ children }) => {
    return <CTableDataCell style={{ position: "relative", fontSize: "1rem" }} className={"RobotoMonoRg"} align='middle'><Link to={`${ufiChecker()}`} onClick={markRestoreOnNextMount}>{children}</Link></CTableDataCell>
  };

  const RobotoMonoRgUpperCaseDataCell = ({ children }) => {
    return <CTableDataCell style={{ textTransform: "uppercase", position: "relative", fontSize: "1rem" }} className={"RobotoMonoRg"} align='middle'><Link to={`${ufiChecker()}`} onClick={markRestoreOnNextMount}>{children}</Link></CTableDataCell>
  };

  return <CTableRow key={`${std}${ufi}`} style={{ borderBottom: "1px solid #D8D8D8" }}>
    <RobotoMonoRgUpperCaseDataCell>{moment.utc(std)?.format("DDMMMYYYY HH:mm") || "--"}</RobotoMonoRgUpperCaseDataCell>
    <RobotoMonoRgDataCell>{aircraftType || "--"}</RobotoMonoRgDataCell>
    <RobotoMonoRgDataCell>{aircraftReg || "--"}</RobotoMonoRgDataCell>
    <CTableDataCell style={{ width: "250px" }}>
      <CXFPConfigTextInput
        key={`${ufi}-${tag ?? ''}`}
        hasLabel={false}
        value={tag}
        dataOnBlur={{
          onBlur: (content: any, objectKey: string) => onBlurTag(content, objectKey, String(tag ?? '')),
          objectKey: ufi,
        }}
      />
    </CTableDataCell>
    <RobotoMonoRgDataCell>{ufi || "--"}</RobotoMonoRgDataCell>
    {/* Hide “FileName” column in table view for temporary */}
    {/* <RobotoMonoRgDataCell>{templateName || "--"}</RobotoMonoRgDataCell> */}
    <RobotoMonoRgDataCell><CRow>
      <CCol>{createdBy || "--"}</CCol>
    </CRow>
      <CRow>
        <CCol>{moment.utc(createTime)?.format("DDMMMYYYY HH:mm") || "--"}</CCol>
      </CRow>
    </RobotoMonoRgDataCell>
    <CTableDataCell align='middle'>
      <div className={"d-flex align-items-center p-0"}>
        <Access route={routePath} access={[AccessType.DELETE]}>
          <div className={"clickable"} style={{ marginLeft: "4px" }}>
            <DeleteIcon
              height={40}
              width={40}
              onClick={(e: any) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                deleteTemplate(ufi);
              }}
            />
          </div>
        </Access>
      </div>
    </CTableDataCell>
    <RobotoMonoRgDataCell>
      <RightArrowLgIcon style={{ position: "absolute", right: "8px", top: "15.5px", bottom: "15.5px" }} width={"31px"} height={"31px"} />
    </RobotoMonoRgDataCell>
  </CTableRow>
};

export default SimOFPTemplateTableItem;
